/* eslint-disable no-magic-numbers */
import * as d3Scale from 'd3-scale';
import { type EChartsType, type GridComponentOption } from 'echarts';
import { BoundingRect, getCoordBoundingRect, niceRange } from '../model/util/coord';
import { getExtremeValues } from '../util/data';
import { axisTypeScaleMap } from '../model/series/dvScatter/DvScatterModel';
import { PARADIGM as mobileParadigm } from '../theme/mobile/series/dvScatter';
import { PARADIGM as pcParadigm } from '../theme/pc/series/dvScatter';

export type ScaleLinear = d3Scale.ScaleLinear<number, number>;
export type ScaleLog = d3Scale.ScaleLogarithmic<number, number>;
export type Scale = ScaleLinear | ScaleLog;

const X_INDEX = 0;
const Y_INDEX = 1;
const R_INDEX = 2;

/**
 * 计算超出grid的距离
 * @param value 三维数据数组
 * @param gridBoundingRect grid外接矩形
 * @param xScale x轴比例尺
 * @param yScale y轴比例尺
 * @param rScale 半径比例尺
 * @returns
 */
export function getOverlapDistance(
  value: unknown[][],
  gridBoundingRect: BoundingRect,
  xScale: Scale,
  yScale: Scale,
  rScale: ScaleLinear
): {
  xOverlap: [number, number];
  yOverlap: [number, number];
} {
  const leftBoundary = gridBoundingRect.x;
  const rightBoundary = gridBoundingRect.x + gridBoundingRect.width;
  const topBoundary = gridBoundingRect.y;
  const bottomBoundary = gridBoundingRect.y + gridBoundingRect.height;
  const xOverlap: [number, number] = [0, 0];
  const yOverlap: [number, number] = [0, 0];

  value.forEach(d => {
    const [xValue, yValue, rValue] = d;
    const xPos = xScale(xValue as number);
    const yPos = yScale(yValue as number);
    const r = rScale(rValue as number);
    if (leftBoundary - (xPos - r) > xOverlap[0]) {
      xOverlap[0] = leftBoundary - (xPos - r);
    }
    if (xPos + r - rightBoundary > xOverlap[1]) {
      xOverlap[1] = xPos + r - rightBoundary;
    }
    if (topBoundary - (yPos - r) > yOverlap[0]) {
      yOverlap[0] = topBoundary - (yPos - r);
    }
    if (yPos + r - bottomBoundary > yOverlap[1]) {
      yOverlap[1] = yPos + r - bottomBoundary;
    }
  });
  return { xOverlap, yOverlap };
}

/**
 * 获取包含气泡在内的轴线范围
 * @param flatValue 三维数据数组，如果包含多个周期，需要先flat处理
 * @param dom  图表的DOM实例或DOMId
 * @param symbolSize 气泡大小,如果采用主题默认配置，则只需要传入主题名称
 * @param config 额外配置项
 */
export function containBubble(
  flatValue: unknown[][],
  dom: HTMLDivElement | string,
  symbolSize: [number, number] | string,
  config?: {
    xAxisOption?: string;
    yAxisOption?: string;
    gridOption?: GridComponentOption;
    xNiceRangeConfig?: Parameters<typeof niceRange>[2];
    yNiceRangeConfig?: Parameters<typeof niceRange>[2];
  }
): { xRange: [number, number]; yRange: [number, number] } {
  let symbolSizeRange = symbolSize;
  if (symbolSize.includes('mobile')) {
    symbolSizeRange = mobileParadigm.symbolSize as [number, number];
  } else if (symbolSize.includes('pc')) {
    symbolSizeRange = pcParadigm.symbolSize as [number, number];
  }

  const {
    xAxisOption = { type: 'value' },
    yAxisOption = { type: 'value' },
    gridOption,
    xNiceRangeConfig,
    yNiceRangeConfig
  } = config || {};
  const xAxisType = xAxisOption.type;
  const yAxisType = yAxisOption.type;
  // const xAxisLogBase = xAxisType === 'log' ? xAxisOption.logBase || 10 : undefined;
  // const yAxisLogBase = yAxisType === 'log' ? yAxisOption.logBase || 10 : undefined;
  // step1 get scale of x,y,r
  let domTemp;
  if (typeof dom === 'string') {
    domTemp = document.querySelector(`#${dom}`);
  } else {
    domTemp = dom;
  }
  const coordBoundingRect = getCoordBoundingRect(domTemp as HTMLDivElement, gridOption);

  const xValueRange = getExtremeValues(flatValue, X_INDEX);
  const yValueRange = getExtremeValues(flatValue, Y_INDEX);
  const rValueRange = getExtremeValues(flatValue, R_INDEX);

  // 仅有一个气泡时，需要特殊处理
  if (xValueRange[0] === xValueRange[1]) {
    if (xValueRange[0] !== 0) {
      xValueRange[0] *= 0.5;
      xValueRange[1] *= 1.5;
    } else {
      xValueRange[0] = -1;
      xValueRange[1] = 1;
    }
  }

  if (yValueRange[0] === yValueRange[1]) {
    if (yValueRange[0] !== 0) {
      yValueRange[0] *= 0.5;
      yValueRange[1] *= 1.5;
    } else {
      yValueRange[0] = -1;
      yValueRange[1] = 1;
    }
  }

  const xPosRange = [coordBoundingRect.x, coordBoundingRect.x + coordBoundingRect.width];
  const yPosRange = [coordBoundingRect.y, coordBoundingRect.y + coordBoundingRect.height];
  const rPosRange = symbolSizeRange as [number, number];

  const xScale = axisTypeScaleMap[xAxisType]().domain(xValueRange).range(xPosRange);
  const yScale = axisTypeScaleMap[yAxisType]().domain(yValueRange).range(yPosRange);
  const rScale = d3Scale.scaleLinear().domain(rValueRange).range(rPosRange);
  // step2 get overlap distance of x,y
  const { xOverlap, yOverlap } = getOverlapDistance(
    flatValue,
    coordBoundingRect,
    xScale,
    yScale,
    rScale
  );
  // step3 transform distance to data
  const xOverlapData = xOverlap.map(d => xScale.invert(d) - xScale.invert(0));
  const yOverlapData = yOverlap.map(d => yScale.invert(d) - yScale.invert(0));
  // step4 get niceRange
  const xValueRangeContainBubble = [
    xValueRange[0] - xOverlapData[0],
    xValueRange[1] + xOverlapData[1]
  ];
  const yValueRangeContainBubble = [
    yValueRange[0] - yOverlapData[0],
    yValueRange[1] + yOverlapData[1]
  ];

  const fixedXRange = niceRange(
    xValueRangeContainBubble[0],
    xValueRangeContainBubble[1],
    xNiceRangeConfig
  );
  const fixedYRange = niceRange(
    yValueRangeContainBubble[0],
    yValueRangeContainBubble[1],
    yNiceRangeConfig
  );
  return {
    xRange: fixedXRange,
    yRange: fixedYRange
  };
}

/**
 * 寻找距离参考点最近的目标数据
 * @param point 参考点坐标
 * @param echartsInstance echarts实例
 * @param seriesIndex series索引
 * @param searchRadius 搜索半径
 * @returns
 */
export function findNearestPointIndex(
  point: { x: number; y: number },
  echartsInstance: EChartsType,
  seriesIndex = 0,
  searchRadius = 24
): { index: number; distance: number } {
  const seriesOption = echartsInstance.getOption().series[seriesIndex];
  if (!seriesOption) {
    console.warn('seriesIndex is out of range');
    return { index: -1, distance: null };
  }

  const { data, dvSymbolSize: symbolSizeRange, dvType, dvSymbol } = seriesOption;
  if (dvType !== 'dvScatter' || dvSymbol !== 'circle') {
    return { index: -1, distance: null };
  }
  const flatValue = data.map(d => {
    return d.value ? d.value : d;
  });

  const rValueRange = getExtremeValues(flatValue, R_INDEX);

  const rScale = d3Scale
    .scaleLinear()
    .domain(rValueRange)
    .range(symbolSizeRange as [number, number]);
  const scatterPos = flatValue.map(d => {
    return echartsInstance.convertToPixel('grid', [d[0] as number, d[1] as number]);
  });
  const distance = scatterPos.map((d, i) => {
    // eslint-disable-next-line no-magic-numbers
    const distanceFromPoint = Math.sqrt((d[0] - point.x) ** 2 + (d[1] - point.y) ** 2);
    const radius = rScale(flatValue[i][2] as number) / 2;

    return {
      centerDistance: distanceFromPoint,
      edgeDistance: distanceFromPoint - radius
    };
  });

  const centerDistance = distance.map(d => d.centerDistance);
  const edgeDistance = distance.map(d => d.edgeDistance);
  // 当鼠标没有在气泡上时，即edgeDistance中没有负数和0时，采用圆边距作为距离，否则采用圆心距
  let distanceTemp = edgeDistance;
  const target = edgeDistance.find(d => d <= 0);
  if (target) {
    distanceTemp = centerDistance;
  }

  // 不能使用排序，会改变索引
  let minIndex = 0;
  distanceTemp.forEach((d, i) => {
    if (d < distanceTemp[minIndex]) {
      minIndex = i;
    }
  });

  return distanceTemp[minIndex] <= searchRadius
    ? { index: minIndex, distance: distanceTemp[minIndex] }
    : { index: -1, distance: null };
}
